import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Lock, CheckCircle2, XCircle, MinusCircle, MapPin } from "lucide-react";
import { playChaChing } from "@/lib/sound";

const resultBadge = {
  won: { icon: CheckCircle2, label: "WON", color: "text-[#4ADE80]", bg: "bg-[#4ADE80]/10" },
  lost: { icon: XCircle, label: "LOST", color: "text-[#F87171]", bg: "bg-[#F87171]/10" },
  void: { icon: MinusCircle, label: "VOID", color: "text-zinc-400", bg: "bg-zinc-800" },
  pending: null,
};

const SEEN_KEY = "dr_seen_wins";
function markSeen(id) {
  const raw = localStorage.getItem(SEEN_KEY);
  const set = new Set(raw ? raw.split(",") : []);
  set.add(id);
  localStorage.setItem(SEEN_KEY, Array.from(set).join(","));
}
function isSeen(id) {
  const raw = localStorage.getItem(SEEN_KEY);
  if (!raw) return false;
  return raw.split(",").includes(id);
}

function fireConfetti(origin) {
  const rect = origin?.getBoundingClientRect?.();
  const originPoint = rect
    ? {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight,
      }
    : { x: 0.5, y: 0.5 };
  const colors = ["#E1FF00", "#D4F000", "#FAFAFA"];
  confetti({
    particleCount: 60,
    spread: 70,
    startVelocity: 32,
    ticks: 180,
    origin: originPoint,
    colors,
    scalar: 0.9,
    gravity: 0.9,
  });
  setTimeout(() => {
    confetti({
      particleCount: 30,
      spread: 100,
      startVelocity: 22,
      ticks: 160,
      origin: originPoint,
      colors,
      scalar: 0.7,
      gravity: 0.9,
    });
  }, 220);
}

export default function LockedTipCard({ tip, unlocked, index = 0 }) {
  const badge = resultBadge[tip.result];
  const ref = useRef(null);

  useEffect(() => {
    if (tip.result === "won" && unlocked && !isSeen(tip.id)) {
      const t = setTimeout(() => {
        fireConfetti(ref.current);
        playChaChing();
        markSeen(tip.id);
      }, 400 + index * 60);
      return () => clearTimeout(t);
    }
  }, [tip.id, tip.result, unlocked, index]);

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 * index, duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
      className={`relative overflow-hidden rounded-2xl border ${
        tip.result === "won" && unlocked ? "border-[#E1FF00]/50" : "border-zinc-800"
      } bg-zinc-900 p-6`}
      data-testid={`tip-card-${tip.id}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Region</div>
          <div className="mt-1 flex items-center gap-2 font-display text-2xl leading-none text-white">
            <MapPin size={16} className="text-[#E1FF00]" /> UK
          </div>
        </div>
        {unlocked ? (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Meeting · Off</div>
            <div className="mt-1 font-mono-data text-lg text-[#E1FF00]">
              {tip.meeting} · {tip.race_time}
            </div>
          </div>
        ) : (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Today</div>
            <div className="mt-1 font-mono-data text-2xl text-[#E1FF00]">🐎</div>
          </div>
        )}
      </div>

      <div className="my-6 h-px w-full bg-zinc-800" />

      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Selection</div>
      <div className="mt-2 flex items-baseline justify-between">
        <div
          className={`font-display text-4xl leading-tight text-white ${unlocked ? "" : "dr-blur-lock"}`}
          data-testid={`tip-selection-${tip.id}`}
        >
          {tip.selection}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 text-sm text-zinc-400">
        <span className="rounded-full border border-zinc-800 px-3 py-1 font-mono-data text-xs text-zinc-300">
          {tip.stake}
        </span>
        <span
          className={`rounded-full border border-zinc-800 px-3 py-1 font-mono-data text-xs ${unlocked ? "text-[#E1FF00]" : "text-zinc-500 dr-blur-lock"}`}
        >
          {tip.odds}
        </span>
        {badge && (
          <span
            className={`ml-auto flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${badge.bg} ${badge.color}`}
          >
            <badge.icon size={12} /> {badge.label}
          </span>
        )}
      </div>

      {unlocked && tip.rationale && (
        <p className="mt-5 border-t border-zinc-800 pt-4 text-sm leading-relaxed text-zinc-400">
          {tip.rationale}
        </p>
      )}

      {!unlocked && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-6">
          <div className="flex items-center gap-2 rounded-full border border-zinc-800 bg-black/80 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 backdrop-blur">
            <Lock size={12} /> Locked · £2 unlocks
          </div>
        </div>
      )}
    </motion.article>
  );
}
